/**
 * Billing calls, shapes copied from workers/api/src/billing (merged on main). The app never computes money:
 * every figure below arrives from the server and is only displayed (R5).
 */
import type { ApiClient } from "./client";

export type Tender = "cash" | "member_credit";
export type PaymentStatus = "unpaid" | "partial" | "paid" | "overpaid";

export interface BillLine {
  id: string; position: number; parentLineId: string | null; itemId: string; itemKind: string; name: string; hsnSac: string | null;
  quantity: number; unitPrice: number; priceIncludesTax: boolean; lineDiscount: number; couponDiscount: number; netExTax: number;
  taxRateBp: number; cgst: number; sgst: number; igst: number; lineTotal: number;
}
export interface BillPayment {
  id: string; billId: string; memberId: string; branchId: string; method: string; amount: number; currency: string; status: string;
  provider: string | null; receivedByStaffId: string | null; businessDate: string; createdAt: number;
}
export interface BillDue { seq: number; dueDate: string; amount: number }

export interface BillSummary {
  id: string; memberId: string; branchId: string; currency: string; status: "issued" | "void";
  subtotalExTax: number; discountTotal: number; taxTotal: number; roundOff: number; total: number;
  paidTotal: number; creditedTotal: number; balance: number; paymentStatus: PaymentStatus;
  buyerName: string | null; businessDate: string; dueDate: string | null; notes: string | null;
  voidedAt: number | null; voidReason: string | null; createdAt: number;
}
/**
 * A bill as `GET /bills` lists it: every money figure is null without `revenue.bill_amounts` (server redaction, D57).
 * A single bill (`GET /bills/:id`) and the export carry them unredacted.
 */
export const BILL_AMOUNT_KEYS = ["subtotalExTax", "discountTotal", "taxTotal", "roundOff", "total", "paidTotal", "creditedTotal", "balance"] as const;
export type Withheld<T, K extends keyof T> = Omit<T, K> & { [P in K]: T[P] | null };
export type BillListRow = Withheld<BillSummary, (typeof BILL_AMOUNT_KEYS)[number]>;
export interface Bill extends BillSummary {
  placeOfSupply: string | null; sellerTaxId: string | null; buyerTaxId: string | null; buyerLegalName: string | null;
  lines: BillLine[]; dues: BillDue[]; payments: BillPayment[];
}

export type LineDiscount = { type: "percent"; bp: number } | { type: "fixed"; amount: number };
export interface CartLine { itemId: string; quantity: number; discount?: LineDiscount }
export type DueSchedule =
  | { type: "equal"; count: number; firstDueDate: string; everyDays?: number; everyMonths?: number }
  | { type: "explicit"; installments: { dueDate: string; amount: number }[] };
export interface Cart {
  memberId: string; branchId: string; lines: CartLine[];
  couponCode?: string; notes?: string; payments?: { method: Tender; amount: number }[]; dueSchedule?: DueSchedule;
}

export interface PlanWarning { lineId: string; itemId: string; warning: unknown }
export interface Preview { preview: true; bill: Bill; warnings: PlanWarning[]; manualDiscountBp: number; walletBalance: number }
export interface Issued { bill: Bill; invoice: { id: string; number: string; docType: string } | null; warnings: PlanWarning[]; walletBalance: number }

export interface Page<K extends string, T> { items: T[]; nextCursor: string | null; key?: K }

export interface BillFilters { memberId?: string; branchId?: string; status?: string; paymentStatus?: string; from?: string; to?: string; cursor?: string; limit?: number }

export const previewBill = (c: ApiClient, cart: Cart, signal?: AbortSignal) => c.request<Preview>("POST", "/bills/preview", { body: cart, signal });
export const issueBill = (c: ApiClient, cart: Cart, idempotencyKey: string) => c.post<Issued>("/bills", cart, { idempotencyKey });

export const listBills = (c: ApiClient, q: BillFilters) =>
  c.get<{ bills: BillListRow[]; nextCursor: string | null }>("/bills", { limit: 50, ...q });
export const getBill = (c: ApiClient, id: string) => c.get<{ bill: Bill }>(`/bills/${id}`).then((r) => r.bill);
export const voidBill = (c: ApiClient, id: string, reason: string) =>
  c.post<{ bill: Bill }>(`/bills/${id}/void`, { reason }).then((r) => r.bill);
export const recordPayment = (c: ApiClient, billId: string, v: { method: Tender; amount: number }, idempotencyKey: string) =>
  c.post<{ payment: BillPayment; paymentStatus: PaymentStatus; balance: number; excess: number }>(`/bills/${billId}/payments`, v, { idempotencyKey });
export const exportBills = (c: ApiClient, q: BillFilters) =>
  c.get<{ bills: BillSummary[]; nextCursor: string | null }>("/bills/export", { limit: 500, ...q });

/** `POST /plans/:id/renew|upgrade`. `expectedVersion` comes from `GET /plans/:id`. */
export interface PlanRef { id: string; itemId: string; kind: string; branchId: string; version: number; memberId?: string; status?: string }
export const getPlan = (c: ApiClient, id: string) => c.get<{ plan: PlanRef & { endDate?: string | null } }>(`/plans/${id}`).then((r) => r.plan);

export interface PlanActionCart {
  couponCode?: string; notes?: string; discount?: LineDiscount;
  payments?: { method: Tender; amount: number }[]; dueSchedule?: DueSchedule;
}
export const renewPlan = (c: ApiClient, planId: string, v: { expectedVersion: number; itemId?: string; branchId?: string; cart?: PlanActionCart }, idempotencyKey: string) =>
  c.post<Issued>(`/plans/${planId}/renew`, v, { idempotencyKey });
export const upgradePlan = (c: ApiClient, planId: string, v: { expectedVersion: number; newItemId: string; asOf: string; cart?: PlanActionCart }, idempotencyKey: string) =>
  c.post<Issued>(`/plans/${planId}/upgrade`, v, { idempotencyKey });
